import { useState, useContext } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { Urls } from "../../../../Urls";
import { UserApiContext } from "../../../UserDetailsContext/UserDetailsContext";
import axios from "../../../../Axios";

const ITEMS_PER_PAGE = 10;

const getMissingFields = (formData) => {
  const missing = [];
  if (!formData.origin) missing.push("origin");
  if (!formData.destination) missing.push("destination");
  if (!formData.truck_types?.value) missing.push("truck_types");
  if (!formData.due_date) missing.push("due_date");
  return missing;
};

const buildLanePayload = (lane, formData) => ({
  ...lane,
  origin: formData.origin,
  destination: formData.destination,
  truck_required: formData.truck_required || "1",
  due_date: formData.due_date,
  truck_types: formData.truck_types || {},
  contract_tenure: [formData.contract_tenure],
  tonnage: formData.capacity,
  tonnage_unit: formData.capacity_unit,
  product_types: formData.product_types,
  additional_details: formData.additional_details,
});

const buildEnquiryLaneDetails = (eld, lanePayload, formData) => ({
  module_key: eld.module_key || "lorri_spot",
  lane_details: lanePayload,
  benchmark_transporter_list: formData.benchmark_transporter_list || [],
  terms_and_condition: eld.terms_and_condition || {},
});

const buildFormDataFromLane = (
  lane,
  missingFields = [],
  benchmarkList = [],
) => ({
  origin: lane.origin || null,
  destination: lane.destination || null,
  truck_types: lane.truck_types?.value ? lane.truck_types : {},
  due_date: lane.due_date || "",
  truck_required: lane.truck_required || "1",
  contract_tenure: lane.contract_tenure?.[0] || {
    label: "Spot Rates",
    value: "Spot Rates",
  },
  benchmark_transporter_list: benchmarkList,
  capacity: lane.tonnage || lane.capacity || "",
  capacity_unit: lane.tonnage_unit || lane.capacity_unit || "MT",
  product_types: lane.product_types || [],
  additional_details: lane.additional_details || "",
});

const useDraftEnquiries = () => {
  const { companyProfile } = useContext(UserApiContext);

  const [editingDraft, setEditingDraft] = useState(null);
  const [page, setPage] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [invalidIds, setInvalidIds] = useState(new Set());

  // ── Fetch draft enquiries ──────────────────────────────────
  const {
    data: draftsResponse,
    isLoading: isDraftsLoading,
    isError: isDraftsError,
    refetch: refetchDrafts,
  } = useQuery({
    queryKey: ["draftEnquiries", page],
    queryFn: async () => {
      const res = await axios.get(`${Urls.spotAgent}/draft_spots`, {
        params: {
          skip: page * ITEMS_PER_PAGE,
          limit: ITEMS_PER_PAGE,
        },
      });
      return res.data;
    },
    placeholderData: (previousData) => previousData,
    refetchOnWindowFocus: false,
  });

  const drafts = draftsResponse?.data || [];
  const totalCount = draftsResponse?.total_count || 0;

  const isAllSelected =
    drafts.length > 0 && drafts.every((d) => selectedIds.has(d.draft_id));
  const isIndeterminate = selectedIds.size > 0 && !isAllSelected;

  // ── Fetch truck types ─────────────────────────────────────
  const { data: truckTypes = [] } = useQuery({
    queryKey: ["truckTypes"],
    queryFn: async () => {
      const res = await axios.get(
        `${Urls.Autocomplete.Get.getlist}?searchFields=truck&include_company_trucks=true`,
      );
      const myTrucks = [];
      const otherTrucks = [];

      res.data?.forEach((item) => {
        if (!item.name) return;
        const option = {
          label: item.from_truck_master ? (
            <span>
              <i
                className="fa fa-truck"
                style={{ marginRight: "8px", color: "#393185" }}
              ></i>
              {item.name}
            </span>
          ) : (
            item.name
          ),
          value: item.name,
          truck_types_group: item.network_truck_type_group,
          isMyTruck: !!item.from_truck_master,
        };
        if (item.from_truck_master) myTrucks.push(option);
        else otherTrucks.push(option);
      });

      return [
        { label: "My trucks", options: myTrucks },
        { label: "Other Trucks", options: otherTrucks },
      ];
    },
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  // ── Fetch material / product types ────────────────────────
  const { data: materialCategories = [] } = useQuery({
    queryKey: ["materialCategories", companyProfile?.domain_data?.company_id],
    queryFn: async () => {
      const companyId = companyProfile?.domain_data?.company_id;
      if (!companyId) return [];
      const { data } = await axios.get(
        `${Urls.spot}?field=get_material_types&company_id=${companyId}`,
      );
      return data?.length > 0 ? data : [];
    },
    enabled: !!companyProfile?.domain_data?.company_id,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  // ── Handlers ─────────────────────────────────────────────
  const handlePageChange = (pageNumber) => {
    setSelectedIds(new Set());
    setPage(pageNumber - 1);
  };

  const handleToggleSelect = (draft_id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(draft_id) ? next.delete(draft_id) : next.add(draft_id);
      return next;
    });
  };

  const handleSelectAll = () => {
    setSelectedIds(
      isAllSelected ? new Set() : new Set(drafts.map((d) => d.draft_id)),
    );
  };

  const handleClearSelection = () => setSelectedIds(new Set());

  const handleBulkReject = async () => {
    if (!selectedIds || selectedIds.size <= 0) return;

    try {
      const { data } = await axios.delete(`${Urls.spotAgent}/draft_spots`, {
        data: { draft_ids: [...selectedIds] },
      });
      refetchDrafts();
      setSelectedIds(new Set());
      toast.success("Draft(s) Rejected Successfully");
    } catch (error) {
      const message =
        error?.response?.data?.message || "Failed to reject draft(s).";
      toast.error(message);
      console.error("[handleBulkReject]", error);
    }
  };

  const handleBulkAccept = async () => {
    if (selectedIds.size === 0) return;

    const selected = drafts.filter((d) => selectedIds.has(d.draft_id));
    const incomplete = selected.filter(
      (d) => (d.missing_fields || []).length > 0,
    );
    const complete = selected.filter(
      (d) => (d.missing_fields || []).length === 0,
    );

    if (incomplete.length > 0) {
      setInvalidIds(new Set(incomplete.map((d) => d.draft_id)));
      toast.error(
        `${incomplete.length} draft(s) have missing fields. Please fix them before accepting.`,
      );
      return;
    }

    const payload = complete.map((d) => ({
      draft_id: d.draft_id,
      enquiry_lane_details: d.enquiry_lane_details,
    }));

    try {
      const { data } = await axios.post(
        `${Urls.spotAgent}/accept_draft`,
        payload,
      );
      if (data?.status === "success") {
        setSelectedIds(new Set());
        refetchDrafts();
        toast.success("Draft(s) Accepted Successfully");
      } else {
        toast.error("Failed to accept draft(s). Please try again.");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to accept drafts.");
    }
  };

  const handleFormChange = (field, value) => {
    setEditingDraft((prev) => ({
      ...prev,
      formData: { ...prev.formData, [field]: value },
    }));
  };

  const handleEdit = (draft) => {
    setEditingDraft({
      ...draft,
      formData: buildFormDataFromLane(
        draft.enquiry_lane_details.lane_details,
        draft.missing_fields || [],
        draft.enquiry_lane_details.benchmark_transporter_list || [],
      ),
    });
  };

  const handleBack = () => setEditingDraft(null);

  // ── Save (PUT) ────────────────────────────────────────────
  const handleSave = async () => {
    const { formData, draft_id, enquiry_lane_details } = editingDraft;
    const lanePayload = buildLanePayload(
      enquiry_lane_details.lane_details,
      formData,
    );
    const enquiryLaneDetails = buildEnquiryLaneDetails(
      enquiry_lane_details,
      lanePayload,
      formData,
    );
    setIsSubmitting(true);
    try {
      await axios.put(`${Urls.spotAgent}/${draft_id}`, {
        enquiry_lane_details: enquiryLaneDetails,
      });
      setEditingDraft((prev) => ({
        ...prev,
        missing_fields: getMissingFields(formData),
        enquiry_lane_details: enquiryLaneDetails,
      }));
      refetchDrafts();
      toast.success("Changes saved successfully.");
    } catch {
      toast.error("Failed to save changes.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Accept ────────────────────────────────────────────────
  // draftOverride: when called directly from the list row (before editingDraft is set)
  const handleAccept = async (draftOverride) => {
    const source = draftOverride ? draftOverride : editingDraft;
    const lane = source.enquiry_lane_details.lane_details;
    const formData =
      source.formData ??
      buildFormDataFromLane(
        lane,
        source.missing_fields || [],
        source.enquiry_lane_details.benchmark_transporter_list || [],
      );
    const missing = getMissingFields(formData);
    if (missing.length > 0) {
      toast.error(`Cannot accept — missing: ${missing.join(", ")}`);
      return;
    }
    const lanePayload = buildLanePayload(lane, formData);
    const enquiryLaneDetails = buildEnquiryLaneDetails(
      source.enquiry_lane_details,
      lanePayload,
      formData,
    );

    const payload = [
      { draft_id: source.draft_id, enquiry_lane_details: enquiryLaneDetails },
    ];
    setIsSubmitting(true);
    try {
      const { data } = await axios.post(
        `${Urls.spotAgent}/accept_draft`,
        payload,
      );

      if (data?.status === "success") {
        toast.success("Changes Saved & Spot Created Successfully");
        setEditingDraft(null);
        refetchDrafts();
      } else {
        toast.error(
          data?.message || "Failed to save changes. Please try again.",
        );
      }
    } catch {
      toast.error("Failed to accept draft. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Reject (DELETE) ───────────────────────────────────────
  const handleReject = async (id) => {
    setIsSubmitting(true);
    try {
      await axios.delete(`${Urls.spotAgent}/draft_spots`, {
        data: { draft_ids: [id] },
      });
      if (editingDraft?.draft_id === id) setEditingDraft(null);
      refetchDrafts();
    } catch {
      toast.error("Failed to reject draft.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    drafts,
    totalCount,
    truckTypes,
    materialCategories,
    editingDraft,
    page,
    isLoading: isDraftsLoading || isSubmitting,
    isDraftsError,
    selectedIds,
    invalidIds,
    isAllSelected,
    isIndeterminate,
    handlePageChange,
    handleFormChange,
    handleEdit,
    handleBack,
    handleSave,
    handleAccept,
    handleReject,
    handleToggleSelect,
    handleSelectAll,
    handleClearSelection,
    handleBulkReject,
    handleBulkAccept,
    refetchDrafts,
  };
};

export default useDraftEnquiries;
